import { ViewUIModel } from '../view/models/view-ui.model';
import { initialViewState } from '../view/state/view.state';
import { UiIO } from '../widget-api/models/ui.io';
import { UrlUIConfig, WidgetUrlConfig } from '../widget-config/initial-config';
import { ViewUI } from '../view/models/view-ui.enum';
import { ScrollModeEnum } from '../engine/models/scroll-mode.enum';
import { InteractTypeEnum } from '../view/models/interact-type.enum';
import { ChapterModel } from '../story/models/chapter.model';
import { get, uniq } from 'lodash';
import { MediaEnum } from '../story/models/media.enum';
import { defaultViewLoader, ViewLoaderEnum } from '../view/models/view-loader.enum';
import { ChapterUIModel } from '../story/models/chapter-ui.model';
import { defaultQuizModeEnum, ViewQuizModeEnum } from '../view/models/view-quiz-mode.enum';
const layoutDefaults = initialViewState.ui;

// Dear future maintainers,
// https://docs.google.com/spreadsheets/d/1SXisKVARVtagG2OKWHzYoqKli6rSs89Y2J020smZkhc/edit?pli=1#gid=1968936664

export class UIConfig {
  [ViewUI.Audio]: boolean | undefined;
  [ViewUI.Camera]: boolean | undefined;
  [ViewUI.Column]: boolean | undefined;
  [ViewUI.Drawer]: boolean | undefined;
  [ViewUI.Fullscreen]: boolean | undefined;
  [ViewUI.Help]: boolean | undefined;
  [ViewUI.Labels]: boolean | undefined;
  [ViewUI.LabelList]: boolean | undefined;
  [ViewUI.Loader]: ViewLoaderEnum | undefined;
  [ViewUI.Logo]: boolean | undefined;
  [ViewUI.Media]: MediaEnum | undefined;
  [ViewUI.Menu]: boolean | undefined;
  [ViewUI.NodeInfo]: boolean | undefined;
  [ViewUI.Pagination]: boolean | undefined;
  [ViewUI.Presenter]: boolean | undefined;
  [ViewUI.Search]: boolean | undefined;
  [ViewUI.TitleOnly]: boolean | undefined;
  [ViewUI.Toolbar]: boolean | undefined;
  [ViewUI.Tools]: boolean | undefined;
  [ViewUI.Undo]: boolean | undefined;

  all: boolean | undefined;
  logoIsHyperLink: boolean | undefined;
  drawerExpandedInitially: boolean | undefined;
  [ViewUI.InternetExplorerWarning]: boolean | undefined;
  [ViewUI.AnatomyLabels]: boolean | undefined;
  [ViewUI.Tutorial]: boolean | undefined;
  [ViewUI.Legend]: boolean | undefined;
  [ViewUI.Whiteboard]: boolean | undefined;
  [ViewUI.VisibilityLayers]: boolean | undefined;
  [ViewUI.ContextActions]: boolean | undefined;
  [ViewUI.ToggleLabels]: boolean | undefined;
}

const setConfig = (_config: boolean, _default: boolean) =>
  _config === undefined ? _default : _config;


function valueForAliases(...aliases: Array<boolean | undefined>) {
  // are any of the aliases true?
  if (aliases.some(alias => alias === true)) {
    return true;
  }

  // are any of the aliases false?
  if (aliases.some(alias => alias === false)) {
    return false;
  }

  // let downstream decide.
  return undefined;
}

export function getMediaTemplate(playPause: boolean, scrubber: boolean, media: MediaEnum): MediaEnum {
  // If template is passed and valid, return template
  if (Object.values(MediaEnum).indexOf(media) >= 0) {
    return media;
  }

  // LEGACY
  // Otherwise, determine template based on legacy boolean properties (playPause, scrubber)

  // Turning off playPause should turn off everything, because you can't have a scrubber without a pause button
  if (playPause === false) {
    return MediaEnum.None;
  }

  // Explicitly enable or disable the scrubber
  if (scrubber != null) {
    return scrubber ? MediaEnum.Full : MediaEnum.Small;
  }

  // If not actionable, do nothing. Allow the app to figure it out.
  return null;
}

export function getLoaderTemplate(loader: ViewLoaderEnum): ViewLoaderEnum {
  return Object.values(ViewLoaderEnum).includes(loader)
    ? loader
    : null;
}

export namespace WidgetWiring {

  // need to map UiIO.UIDisplayParams and UrlUIConfig to UIConfig
  export function convertAPIUIConfigToUIConfig(apiConfig: UiIO.UIDisplayParams): UIConfig {
    const uiConfig = (apiConfig['all'] != null) ? generateUIAllConfig(apiConfig['all']) : new UIConfig();
    const apiUIConfig = {
      all: apiConfig['all'],
      [ViewUI.Loader]: getLoaderTemplate(apiConfig['loader']),
      [ViewUI.Help]: valueForAliases(
        apiConfig['help'],
        (apiConfig as UiIO.UIDisplayParamsInternal).help
      ),
      [ViewUI.Tools]: valueForAliases(
        apiConfig['tools'],
        (apiConfig as UiIO.UIDisplayParamsInternal).tools
      ),
      [ViewUI.Layout]: apiConfig['layout'],
      [ViewUI.Toolbar]: apiConfig['toolbar'],
      [ViewUI.Fullscreen]: valueForAliases(
        apiConfig['fullscreen'],
        (apiConfig as UiIO.UIDisplayParamsInternal).fullscreen
      ),
      [ViewUI.Search]: valueForAliases(
        apiConfig['objectTree'],
        (apiConfig as UiIO.UIDisplayParamsInternal).search
      ),
      [ViewUI.Audio]: apiConfig['audio'],
      [ViewUI.Media]: getMediaTemplate(
        apiConfig['playPause'],
        valueForAliases(
          apiConfig['scrubber'],
          apiConfig['scrubber.chapters'],
          
        ),
        (apiConfig as UiIO.UIDisplayParamsInternal).media
      ),
      [ViewUI.Camera]: valueForAliases(
        apiConfig['nav'],
        apiConfig['cameraMode'],
        apiConfig['center'],
        apiConfig['zoomIn'],
        apiConfig['zoomOut'],
        (apiConfig as UiIO.UIDisplayParamsInternal).nav
      ),
      [ViewUI.Pagination]: valueForAliases(
        apiConfig['tour'],
        apiConfig['tour.chapters']
      ),

      [ViewUI.Column]: valueForAliases(
        apiConfig['info'],
        (apiConfig as UiIO.UIDisplayParamsInternal).info
      ),
      [ViewUI.Drawer]: valueForAliases(
        apiConfig['info'],
        (apiConfig as UiIO.UIDisplayParamsInternal).info
      ),
      [ViewUI.TitleOnly]: valueForAliases(
        apiConfig['info'],
        (apiConfig as UiIO.UIDisplayParamsInternal).info
      ),

      [ViewUI.LabelList]: valueForAliases(
        apiConfig['labelList'],
        (apiConfig as UiIO.UIDisplayParamsInternal).labelList
      ),
      [ViewUI.NodeInfo]: (apiConfig as UiIO.UIDisplayParamsInternal).anatomyDescriptions,
      [ViewUI.Legend]: (apiConfig as UiIO.UIDisplayParamsInternal).legend,

      [ViewUI.Menu]: (apiConfig as UiIO.UIDisplayParamsInternal).menu,

      [ViewUI.InternetExplorerWarning]: undefined,

      // not controlled by API]
      [ViewUI.Labels]: undefined,
      [ViewUI.Logo]: undefined,
      [ViewUI.Presenter]: undefined,
      logoIsHyperLink: undefined,
      drawerExpandedInitially: undefined,
      [ViewUI.Tutorial]: undefined,
      [ViewUI.AnatomyLabels]: valueForAliases(apiConfig['anatomyLabels'], (apiConfig as UiIO.UIDisplayParamsInternal).anatomyLabels),
      [ViewUI.Undo]: valueForAliases(apiConfig['reset'], apiConfig['undo']),
      [ViewUI.Whiteboard]: apiConfig['whiteboard'],
      [ViewUI.VisibilityLayers]: apiConfig['visibilityLayers'],
      [ViewUI.ContextActions]: apiConfig['contextActions'],
      [ViewUI.ToggleLabels]: undefined,
      // [ViewUI.QuizMode]: undefined,
      // [ViewUI.QuizNumber]: undefined
    };

    return mergeUIConfigs(apiUIConfig, uiConfig);
  }


  export function convertUrlUIConfigToUIConfig(uriConfig: UrlUIConfig): UIConfig {
    // Check all config
    const uiConfig = (uriConfig['ui-all'] != null) ? generateUIAllConfig(uriConfig['ui-all']) : new UIConfig();

    const urlUIConfig = {
      [ViewUI.Layout]: uriConfig['ui-layout'],
      [ViewUI.Loader]: getLoaderTemplate(uriConfig['ui-loader']),
      [ViewUI.NodeInfo]: uriConfig['ui-anatomy-descriptions'],
      [ViewUI.Tools]: uriConfig['ui-tools'],
      [ViewUI.Toolbar]: uriConfig['ui-toolbar'],
      [ViewUI.Media]: getMediaTemplate(
        null,
        valueForAliases(
          uriConfig['ui-scrubber'],
          uriConfig['ui-scrubber.chapters'],
          uriConfig['ui-scrubber.tour']
        ),
        uriConfig['ui-media-controls']
      ),
      [ViewUI.Audio]: uriConfig['ui-audio'],
      [ViewUI.Fullscreen]: uriConfig['ui-fullscreen'],
      [ViewUI.Help]: uriConfig['ui-help'],
      [ViewUI.Camera]: uriConfig['ui-nav'],
      [ViewUI.Presenter]: uriConfig['ui-presenter'],
      [ViewUI.Menu]: uriConfig['ui-menu'],
      [ViewUI.Pagination]: uriConfig['ui-tour'],
      // handle params with aliases
      [ViewUI.Search]: valueForAliases(
        uriConfig['ui-search'],
        uriConfig['ui-object-tree']
      ),
      [ViewUI.Column]: valueForAliases(
        uriConfig['ui-info'],
        uriConfig['ui-panel']
      ),
      [ViewUI.Drawer]: valueForAliases(
        uriConfig['ui-info'],
        uriConfig['ui-panel']
      ),
      [ViewUI.Description]: valueForAliases(
        uriConfig['ui-info']
      ),
      [ViewUI.TitleOnly]: valueForAliases(
        uriConfig['ui-info']
      ),
      [ViewUI.Logo]: valueForAliases(uriConfig['ui-bd'], uriConfig['ui-logo']),
      logoIsHyperLink: valueForAliases(
        uriConfig['ui-bd.link'],
        uriConfig['ui-logo.islink']
      ),
      [ViewUI.Labels]: uriConfig['ui-labels'],
      [ViewUI.LabelList]: uriConfig['ui-label-list'],
      [ViewUI.InternetExplorerWarning]: uriConfig['ie-warning'],
      all: uriConfig['ui-all'],
      drawerExpandedInitially: uriConfig['ui-info-expand'],
      [ViewUI.AnatomyLabels]: valueForAliases(uriConfig['ui-anatomy-labels']),
      [ViewUI.Tutorial]: valueForAliases(uriConfig['ui-tutorial']),
      [ViewUI.Legend]: valueForAliases(uriConfig['ui-legend']),
      [ViewUI.Undo]: valueForAliases(uriConfig['ui-undo'], uriConfig['ui-reset']),
      [ViewUI.Whiteboard]: valueForAliases(uriConfig['ui-whiteboard']),
      [ViewUI.VisibilityLayers]: valueForAliases(
        uriConfig['ui-visibility-layers'],
        uriConfig['ui-layers']
      ),
      [ViewUI.ContextActions]: valueForAliases(uriConfig['ui-context-actions']),
      [ViewUI.ToggleLabels]: undefined
    };

    return mergeUIConfigs(urlUIConfig, uiConfig);
  }

  export function chapterUItoUIConfig(ui: ChapterUIModel): UIConfig {
    if (!ui || !ui.config) {
      // TODO: why not just return a default UIConfig object?
      return null;
    }

    const uiConfig = new UIConfig();
    // const config = getUIConfigForUIAll(true);
    
    uiConfig[ViewUI.Column] = ui.config.info;
    uiConfig[ViewUI.Drawer] = ui.config.info;
    uiConfig[ViewUI.Fullscreen] = ui.config.fullscreen;
    uiConfig[ViewUI.Search] = ui.config.objectTree;
    uiConfig[ViewUI.Camera] = ui.config.nav;
    uiConfig[ViewUI.Loader] = getLoaderTemplate(ui.config.loader);
    uiConfig[ViewUI.Whiteboard] = ui.config.whiteboard;
    uiConfig[ViewUI.VisibilityLayers] = ui.config.visibilityLayers;
    uiConfig[ViewUI.ContextActions] = ui.config.contextActions;

    uiConfig[ViewUI.Media] = getMediaTemplate(
      ui.config.playPause,
      ui.config.scrubber,
      ui.config.mediaControls
    );

    return uiConfig;
  }

  // Generates a stock UI config from an 'all' parameter
  export function generateUIAllConfig(all: boolean): UIConfig {
    return {
      [ViewUI.Loader]: (all ? defaultViewLoader : ViewLoaderEnum.None),
      [ViewUI.Column]: all,
      [ViewUI.Drawer]: all,
      [ViewUI.Tools]: all,
      [ViewUI.Toolbar]: all,
      [ViewUI.Fullscreen]: all,
      [ViewUI.Help]: all,
      [ViewUI.Camera]: all,
      [ViewUI.Search]: all,
      [ViewUI.Presenter]: all,
      [ViewUI.Menu]: all,
      [ViewUI.Audio]: all,
      [ViewUI.Pagination]: all,
      [ViewUI.NodeInfo]: all,
      [ViewUI.Labels]: null,
      [ViewUI.LabelList]: all,
      [ViewUI.Media]: null,
      [ViewUI.TitleOnly]: all,
      [ViewUI.Undo]: all,
      all: all,
      drawerExpandedInitially: null,
      logoIsHyperLink: null,
      [ViewUI.AnatomyLabels]: null,
      [ViewUI.Logo]: true,
      [ViewUI.Tutorial]: null,
      [ViewUI.InternetExplorerWarning]: true,
      [ViewUI.Legend]: true,
      [ViewUI.Whiteboard]: all,
      [ViewUI.VisibilityLayers]: all,
      [ViewUI.ContextActions]: all,
      [ViewUI.ToggleLabels]: true,
      // [ViewUI.QuizMode]: defaultQuizModeEnum,
      // [ViewUI.QuizNumber]: null
    };
  }

  export function generateQuizUIConfig() {
    const uiConfig = new UIConfig();
    uiConfig.search = false;
    uiConfig.pagination = false;
    uiConfig.labelList = false;
    uiConfig.labels = false;
    uiConfig.anatomyLabels = false;
    uiConfig.nodeInfo = false;
    uiConfig.toggleLabels = false;
    uiConfig.legend = false;

    return uiConfig;
  }

  export function mergeUIConfigs(primary: UIConfig, secondary: UIConfig, force = false) {
    if (secondary == null) {
      return primary;
    }

    const keys = uniq([...Object.keys(primary), ...Object.keys(secondary)]);
    return keys.reduce((config, key) => {
      // Default to the primary key
      // Use the secondary key if the primary key is null
      // Use the secondary key if force is true, and the secondary key is not null
      config[key] = (primary[key] == null || (force && secondary[key] != null)) ? secondary[key] : primary[key];
      
      return config;
    }, new UIConfig());
  }

  export function createLayoutFromConfig(
    currentLayout: ViewUIModel,
    config: UIConfig
  ): ViewUIModel {
    const newLayout: ViewUIModel = JSON.parse(JSON.stringify(currentLayout));
    
    // Note: UIConfig.all should never be used here. The `all` property is handled during
    // uiConfig generation. 

    newLayout.loader.enable = setConfig(
      config[ViewUI.Loader] !== ViewLoaderEnum.None,
      layoutDefaults.loader.enable
    );

    if (config[ViewUI.Loader] != null) {
      newLayout.loader.template = config[ViewUI.Loader];
    }

    newLayout.nodeInfo.enable = setConfig(
      config[ViewUI.NodeInfo],
      layoutDefaults.nodeInfo.enable
    );

    newLayout.pagination.enable = setConfig(
      config[ViewUI.Pagination],
      layoutDefaults.pagination.enable
    );

    newLayout.drawer.isExpandedInitially = setConfig(
      config.drawerExpandedInitially,
      layoutDefaults.drawer.isExpandedInitially
    );

    newLayout.tools.enable = setConfig(
      config[ViewUI.Tools],
      layoutDefaults.tools.enable
    );

    newLayout.toolbar.enable = setConfig(
      config[ViewUI.Toolbar],
      layoutDefaults.toolbar.enable
    );

    newLayout.media.enable = setConfig(
      config[ViewUI.Media] !== MediaEnum.None,
      layoutDefaults.media.enable
    );
    newLayout.media.template = config[ViewUI.Media];

    newLayout.fullscreen.enable = setConfig(
      config[ViewUI.Fullscreen],
      layoutDefaults.fullscreen.enable
    );

    newLayout.help.enable = setConfig(
      config[ViewUI.Help],
      layoutDefaults.help.enable
    );

    newLayout.description.enable = setConfig(
      config[ViewUI.Description],
      layoutDefaults.description.enable
    );

    newLayout.titleOnly.enable = setConfig(
      config[ViewUI.TitleOnly],
      layoutDefaults.titleOnly.enable
    );

    newLayout.camera.enable = setConfig(
      config[ViewUI.Camera],
      layoutDefaults.camera.enable
    );

    newLayout.search.enable = setConfig(
      config[ViewUI.Search],
      layoutDefaults.search.enable
    );

    newLayout.presenter.enable = setConfig(
      config[ViewUI.Presenter],
      layoutDefaults.presenter.enable
    );

    newLayout.menu.enable = setConfig(
      config[ViewUI.Menu],
      layoutDefaults.menu.enable
    );

    newLayout.logo.enable = setConfig(
      config[ViewUI.Logo],
      layoutDefaults.logo.enable
    );

    newLayout.logo.isHyperLink = setConfig(
      config.logoIsHyperLink,
      layoutDefaults.logo.isHyperLink
    );

    newLayout.internetExplorerWarning.enable = setConfig(
      config.internetExplorerWarning,
      layoutDefaults.internetExplorerWarning.enable
    );

    newLayout.undo.enable = setConfig(
      config[ViewUI.Undo],
      layoutDefaults.undo.enable
    );

    newLayout.labels.enable = setConfig(config[ViewUI.Labels], layoutDefaults.labels.enable);
    newLayout.labelList.enable = setConfig(config[ViewUI.LabelList], layoutDefaults.labelList.enable);

    newLayout.audio.enable = setConfig(config[ViewUI.Audio], layoutDefaults.audio.enable);
    newLayout.reset.enable = setConfig(config[ViewUI.Reset], layoutDefaults.reset.enable);

    newLayout.column.enable = setConfig(config[ViewUI.Column], layoutDefaults.column.enable);
    newLayout.drawer.enable = setConfig(config[ViewUI.Drawer], layoutDefaults.drawer.enable);

    newLayout.anatomyLabels.enable = setConfig(config[ViewUI.AnatomyLabels], layoutDefaults.anatomyLabels.enable);
    newLayout.tutorial.enable = setConfig(config[ViewUI.Tutorial], layoutDefaults.tutorial.enable);
    newLayout.whiteboard.enable = setConfig(config[ViewUI.Whiteboard], layoutDefaults.whiteboard.enable);

    newLayout.visibilityLayers.enable = setConfig(config[ViewUI.VisibilityLayers], layoutDefaults.visibilityLayers.enable);

    newLayout.contextActions.enable = setConfig(config[ViewUI.ContextActions], layoutDefaults.contextActions.enable);
    newLayout.toggleLabels.enable = setConfig(config[ViewUI.ToggleLabels], layoutDefaults.toggleLabels.enable);

    return newLayout;
  }


  export function getScrollSetting(widgetConfig: WidgetUrlConfig) {
    if (widgetConfig['disable-scroll']) {
      return ScrollModeEnum.DoNotCapture;
    }

    if (widgetConfig['disable-scroll.alert'] || widgetConfig['disable-scroll.overlay']) {
      return ScrollModeEnum.DoNotCaptureShowMessage;
    }

    return ScrollModeEnum.Capture;
  }

  export function getUsePrintImage(widgetConfig: WidgetUrlConfig) {
    return widgetConfig['ui-print-image'] === true;
  }

  export function getInteractType(widgetConfig: WidgetUrlConfig, interactActive: boolean, currentInteractType: InteractTypeEnum) {
    const isInteractActive =
      widgetConfig['initial.play'] ||
      widgetConfig['initial'] ||
      widgetConfig['initial.button'] ||
      widgetConfig['initial.hand'] ||
      widgetConfig['initial.hand-hint'] ||
      interactActive;

    const interactType = (() => {
      switch (true) {
        case widgetConfig['initial']:
        case widgetConfig['initial.button']:
          return InteractTypeEnum.Button;

        case widgetConfig['initial.hand']:
        case widgetConfig['initial.hand-hint']:
          return InteractTypeEnum.Hand;

        case widgetConfig['initial.play']:
          return InteractTypeEnum.Play;

        default:
          return currentInteractType;
      }
    })();

    return { interactType, isInteractActive };
  }

  export function getOnDemand(widgetConfig: WidgetUrlConfig) {
    return widgetConfig['auto-load'] === false;
  }
}
